<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Add Reward
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="4">
              <v-autocomplete
                v-model="form.week_id"
                :items="weekItems"
                outlined
                label="Week"
                prepend-inner-icon="mdi-calendar-week"
                clearable
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-divider class="my-5"> </v-divider>

          <section v-if="form.week_id">
            <!-- Days Section -->
            <div v-for="(day, dayIndex) in form.days" :key="`day${dayIndex}`">
              <v-card class="mb-4" outlined>
                <v-card-title class="py-2">
                  <v-row align="center">
                    <v-col sm="4">
                      <v-autocomplete
                        v-model="day.day_number_id"
                        :items="availableDayItems(dayIndex)"
                        outlined
                        dense
                        label="Day Number"
                        prepend-inner-icon="mdi-calendar-today"
                        clearable
                      ></v-autocomplete>
                    </v-col>
                    <v-col sm="2">
                      <v-dialog
                        v-model="isDeleteDayDialogs[dayIndex]"
                        max-width="600px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            small
                            dark
                            color="red"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small> mdi-delete </v-icon>
                            Remove Day
                          </v-btn>
                        </template>
                        <v-card elevation="10">
                          <v-card-title>
                            <v-icon large color="primary">
                              mdi-information-outline
                            </v-icon>
                            &nbsp; Do you want to remove this day?
                          </v-card-title>
                          <v-card-text>
                            <v-btn
                              color="primary"
                              @click.stop="deleteDay(dayIndex)"
                            >
                              Yes
                            </v-btn>
                            &nbsp;
                            <v-btn
                              color="red"
                              dark
                              @click.stop="isDeleteDayDialogs = []"
                            >
                              No
                            </v-btn>
                          </v-card-text>
                        </v-card>
                      </v-dialog>
                    </v-col>
                  </v-row>
                </v-card-title>

                <v-card-text v-if="day.day_number_id">
                  <h4 class="mb-3">
                    Rewards for Day {{ getDayText(day.day_number_id) }}
                  </h4>

                  <!-- Rewards for this day -->
                  <v-row
                    v-for="(reward, rewardIndex) in day.rewards"
                    :key="`reward${dayIndex}_${rewardIndex}`"
                  >
                    <v-col sm="3">
                      <v-autocomplete
                        v-model="reward.reward_type_id"
                        :items="rewardTypesItems"
                        outlined
                        dense
                        label="Reward Type"
                        prepend-inner-icon="mdi-gift"
                        clearable
                      ></v-autocomplete>
                    </v-col>
                    <v-col sm="3" v-if="reward.reward_type_id == 25">
                      <v-text-field
                        v-model="reward.reward_value"
                        outlined
                        dense
                        label="Reward Value"
                        type="number"
                        prepend-inner-icon="mdi-currency-usd"
                      ></v-text-field>
                    </v-col>
                    <v-col sm="2" v-if="reward.reward_type_id == 24">
                      <v-autocomplete
                        v-model="reward.store_item_id"
                        :items="storeItems"
                        item-text="text"
                        item-value="id"
                        outlined
                        dense
                        label="Store Item"
                        prepend-inner-icon="mdi-package-variant"
                        clearable
                      />
                    </v-col>
                    <v-col sm="2" v-if="reward.reward_type_id == 24">
                      <v-text-field
                        v-model="reward.expiry_date"
                        outlined
                        dense
                        label="Expiry Date"
                        type="date"
                        prepend-inner-icon="mdi-calendar-clock"
                        :min="minDate"
                        required
                      />
                    </v-col>
                    <v-col sm="1">
                      <v-dialog
                        v-model="
                          isDeleteRewardDialogs[`${dayIndex}_${rewardIndex}`]
                        "
                        max-width="600px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            fab
                            small
                            dark
                            color="red"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon dark> mdi-close-thick </v-icon>
                          </v-btn>
                        </template>
                        <v-card elevation="10">
                          <v-card-title>
                            <v-icon large color="primary">
                              mdi-information-outline
                            </v-icon>
                            &nbsp; Do you want to remove this reward?
                          </v-card-title>
                          <v-card-text>
                            <v-btn
                              color="primary"
                              @click.stop="deleteReward(dayIndex, rewardIndex)"
                            >
                              Yes
                            </v-btn>
                            &nbsp;
                            <v-btn
                              color="red"
                              dark
                              @click.stop="isDeleteRewardDialogs = {}"
                            >
                              No
                            </v-btn>
                          </v-card-text>
                        </v-card>
                      </v-dialog>
                    </v-col>
                  </v-row>

                  <!-- Add Reward Button for this day -->
                  <v-row>
                    <v-col sm="12" class="mt-2">
                      <v-btn
                        x-small
                        rounded
                        color="secondary"
                        @click="addRewardToDay(dayIndex)"
                      >
                        Add Reward
                        <v-icon small dark> mdi-plus </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </div>

            <!-- Add Day Button -->
            <v-row>
              <v-col sm="12" class="mb-5">
                <v-btn small rounded color="primary" @click="addNewDay">
                  Add Day
                  <v-icon dark> mdi-plus </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-divider class="mt-5 mb-5"> </v-divider>
          </section>
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            Save Reward
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";

export default {
  name: "RewardsCreatePage",
  data() {
    return {
      form: {
        days: [],
        week_id: "",
        is_active: true,
      },
      reward_image: null,
      isLoading: false,
      dayItems: [],
      rewardTypesItems: [],
      isDeleteRewardDialogs: {},
      isDeleteDayDialogs: [],
      weekItems: [],
      storeItems: [],
      minDate: new Date().toISOString().split("T")[0],
    };
  },
  mounted() {
    this.getMasters();
  },
  methods: {
    async getMasters() {
      await axios
        .get(`masters?context=rewards`)
        .then((masters) => {
          masters = masters.data;

          // Days
          masters.days.forEach((day) => {
            this.dayItems.push({
              id: day.id,
              text: day.description,
              value: day.id,
            });
          });

          // Weeks
          masters.weeks.forEach((week) => {
            this.weekItems.push({
              id: week.id,
              text: week.description,
              value: week.id,
            });
          });

          // Reward Types
          masters.reward_types.forEach((reward_type) => {
            this.rewardTypesItems.push({
              id: reward_type.id,
              text: reward_type.description,
              value: reward_type.id,
            });
          });
          // store Item
          masters.storeItem.forEach((storeItem) => {
            this.storeItems.push({
              id: storeItem.id,
              text: storeItem.name,
              value: storeItem.id,
            });
          });
          console.log('storeItem',this.storeItems);
          
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    async save() {
      try {
        this.isLoading = true;

        // Flatten the days structure to rewards array for API
        const rewards = [];
        this.form.days.forEach((day) => {
          day.rewards.forEach((reward) => {
            rewards.push({
              day_number_id: day.day_number_id,
              reward_type_id: reward.reward_type_id,
              reward_value: reward.reward_value,
              store_item_id: reward.store_item_id,
              expiry_date: reward.expiry_date,
            });
          });
        });

        const payload = {
          week_id: this.form.week_id,
          is_active: this.form.is_active,
          rewards: rewards,
        };

        let response = await axios.post("/daily_rewards", payload);
        console.log("rewards", response.data);
        this.isLoading = false;
        this.$router.back();
      } catch (e) {
        console.log(e);
        this.isLoading = false;
      }
    },
    addNewDay() {
      let day = {
        day_number_id: "",
        rewards: [],
      };
      this.form.days.push(day);
    },
    addRewardToDay(dayIndex) {
      let reward = {
        reward_type_id: "",
        reward_value: "",
        store_item_id: "",
        expiry_date: "",
      };
      this.form.days[dayIndex].rewards.push(reward);
    },
    deleteDay(dayIndex) {
      this.form.days.splice(dayIndex, 1);
      this.isDeleteDayDialogs = [];
    },
    deleteReward(dayIndex, rewardIndex) {
      this.form.days[dayIndex].rewards.splice(rewardIndex, 1);
      this.isDeleteRewardDialogs = {};
    },
    availableDayItems(currentDayIndex) {
      const selectedDays = this.form.days
        .map((day, index) =>
          index !== currentDayIndex ? day.day_number_id : null
        )
        .filter((id) => id !== null);

      return this.dayItems.filter((item) => !selectedDays.includes(item.value));
    },
    getDayText(dayId) {
      const day = this.dayItems.find((item) => item.value === dayId);
      return day ? day.text : "";
    },
  },
};
</script>
